import React, { useEffect, useMemo, useRef, useState } from 'react';
import '../../base.scss';
import './Table.scss';
import { SortIcon } from '../SortIcon/SortIcon';
import { classNames } from '../../utilities';
import { Select } from '../Select';
import { Button } from '../Button';
import { Input } from '../Input';
import { Checkbox } from '../Checkbox';
import usePrevious from '../../utilities/hooks/use-previous';
import useDidMountEffect from '../../utilities/hooks/use-did-mount-effect';

import { useSticky } from 'react-table-sticky';

import ContentLoader from 'react-content-loader';

import {
  CellProps,
  Column,
  useTable,
  useResizeColumns,
  useSortBy,
  useFlexLayout,
  PluginHook,
  usePagination,
  useRowSelect,
  HeaderProps,
  Row,
  IdType,
  useMountedLayoutEffect,
  TableHeaderProps as RTTableHeaderProps,
  TableState,
  TableInstance,
} from 'react-table';

interface TableHeaderProps extends RTTableHeaderProps {
  'data-sticky-td': boolean;
}

export interface ManualPaginationProps {
  /** Should be used with useCallback that can set dependencies to determine when to run.
   *  In most cases it should listen to anything in the table's state.
   */
  updatePageData: (pageIndex?: number, pageSize?: number) => void;
  /** Function called when a new page is requested.
   *  This function should update the controlled state with the new pagination state. */
  onPaginate: (pageIndex: number, pageSize: number) => void;
}

/** Options to customize pagination behavior */
export interface PaginationProps {
  /**
   * Height of a row when loading
   * @default 42.79
   */
  loadingRowHeight?: number;
  /** Initial page to load if not using ManualPagination.
   *  In the case of ManualPagination, this should be updated
   *  with the controlled pageIndex state from a parent component.
   * @default 0
   */
  pageIndex?: number;
  /** Initial page size to show if not using ManualPagination.
   *  In the case of ManualPagination, this should be updated
   *  with the controlled pageSize state from a parent component.
   * @default 10
   */
  pageSize?: number;
  /** If the state of the table should be controlled through
   *  a parent component, define the required props in manualPagination
   *  and provide a passed pageIndex and pageSize as needed.
   */
  manualPagination?: ManualPaginationProps;
  /** When true, a loading state is displayed to the user. Use while waiting for new page data.
   * @default false
   */
  pageIsLoading?: boolean;
  /** Page size options available to the user
   * @default [10,50,100]
   */
  pageSizeOptions?: number[];
  /** Total number of pages, requred when using manual pagination */
  totalPageCount?: number;
  /** Total number of rows, required when using manual pagination */
  totalRowCount?: number;
  /** Determines wheter the page size Select component should drop up */
  dropUp?: boolean;
}

export interface ToolbarAction<T> {
  /** Toolbar action name to be displayed in the button */
  actionName: string;
  /** Name of the icon to be displayed in toolbar action */
  actionIconName?: string;
  /** Callback for the toolbar action */
  onAction: (selectedRows: T[], selectedRowIds?: string[]) => void;
  /** Function to define whether selected rows support the action */
  isActionEnabled: (selectedRows: T[]) => boolean;
  /** Whether the selected rows should be cleared after the action */
  clearSelections?: boolean;
}

/** Options to customize a toolbar
 * Toolbar shows up ONLY when row selection is enabled (rowSelectionEnabled = true)
 */
export interface ToolbarProps<T> {
  /** Defines if the toolbar is hidden until user starts selecting rows.
   * @default false
   */
  isHiddenUntilSelection?: boolean;
  /** Toolbar actions */
  actions: ToolbarAction<T>[];
}

/**
 * Represents a column in your table. The most basic column
 * will specify an 'id' and 'name'
 */
export interface TableColumn {
  /** Whether or not to align left or right, defaults to left if not passed in
   * @default 'left'
   */
  contentAlign?: 'left' | 'right';
  /** Sub-columns, used to support nesting columns underneath this header */
  columns?: TableColumn[];
  /** Class to apply to all cells in this column */
  columnClassName?: string;
  /** Describes this column's data, used for default styling */
  dataType?: 'text' | 'numeric' | 'currency' | 'date';
  /** Class to apply only the column's header cell */
  headerClassName?: string;
  /** Unique key for this column, don't set for headers with nested columns */
  id?: string;
  /** Whether to allow sorting on this column */
  isSortable?: boolean;
  /** Max width of this column (used for resizing) */
  maxWidth?: number;
  /** Min width of this column (used for resizing) */
  minWidth?: number;
  /** Displayed name of this column */
  name: string;
  /** Sets the fixed behavior of this column */
  sticky?: 'left' | 'right';
  /** Sets a width for this column (rather than using flex width) */
  width?: number;
  /** A render function which returns what should be displayed in a cell, given the cell's value */
  formatCell?: (value: any, row?: TableRow) => React.ReactElement | string | number; // eslint-disable-line @typescript-eslint/no-explicit-any
  /** A render function which returns what should be displayed in a header, given the header cell's value */
  formatHeader?: (value: any) => React.ReactElement | string | number; // eslint-disable-line @typescript-eslint/no-explicit-any
}

/**
 * Wrapper for a row type, to provide common row functionality
 */
export interface TableRow extends Record<string, unknown> {
  /** Class applied to the entire table row */
  className?: string;
  dataType?: 'text' | 'numeric' | 'currency' | 'date';
}

const initialSelectedRowIds: Record<IdType<TableRow>, boolean> = {};

/**
 * Describes your table's configuration, columns and data.
 *
 * The simplest table will have 'columns' and 'data' specified.
 */
export interface TableProps<T extends TableRow> {
  /** Class to apply to the table itself */
  className?: string;
  /** Table columns. Each must have an id corresponding to a property in the row type */
  columns: TableColumn[];
  /** Whether the table should display compactly (shorter rows) */
  compact?: boolean;
  /** Optional component to render for empty table state */
  emptyComponent?: React.ReactElement;
  /** An array of objects which describe table rows, where T extends TableRow
   * MUST BE a memoized array (useMemo())
   */
  data: T[];
  /** Whether to have the headers fixed when scrolling */
  fixedHeader?: boolean;
  /** [Not yet implemented] Whether the columns should be resizeable by the user */
  isResizable?: boolean;
  /** Whether the table supports sorting */
  sortEnabled?: boolean;
  /** Optional rowStripingType to apply */
  rowStripingType?: '' | 'striped-odd' | 'striped-even';
  /** Whether row selection is active
   * @default false
   */
  rowSelectionEnabled?: boolean;
  /** Callback for sorting if sorting should not be handled by the table */
  onSort?: (columnId: string, direction: 'ascending' | 'descending') => void;
  /** Callback when rows are selected or deselected - MUST BE a memoized implementation (useCallback()) */
  onSelectRow?: (selectedRows: T[]) => void;
  /** Whether the table should use frontend pagination
   * @default false
   */
  enablePagination?: boolean;
  /** Pagination options to use (if enablePagination is true) */
  paginationProps?: PaginationProps;
  /** Table toolbar */
  toolbar?: ToolbarProps<T>;
  /** Use this function to change how React Table detects unique rows and also how it constructs each row's underlying id property. */
  getRowId?: (row: TableRow) => string;
  /** Optional callback to interact with the table instance */
  onTableInstanceChange?: (instance: TableInstance<T>) => void;
}

export function Table<T extends TableRow>(props: TableProps<T>) {
  const [sortColumn, setSortColumn] = useState<string>();
  const [sortDirection, setSortDirection] = useState<string>();
  const [paginationInputValue, setPaginationInputValue] = useState<number | string>('1');

  const { onSelectRow, onTableInstanceChange } = props;

  const pageSizeOptionsSelectRef = useRef(null);
  const paginationInputRef = useRef<HTMLInputElement>(null);
  const paginationProps = props.paginationProps;
  const hasManualPagination = !!paginationProps?.manualPagination?.updatePageData;
  const updatePageData = paginationProps?.manualPagination?.updatePageData;
  const onPaginate = paginationProps?.manualPagination?.onPaginate;
  const totalPageCount = paginationProps?.totalPageCount ?? -1;
  const totalRowCount = paginationProps?.totalRowCount ?? -1;

  if (hasManualPagination && !(totalPageCount >= 0)) {
    throw new Error('Invalid props: using updatePageData requires that totalPageCount be set');
  } else if (hasManualPagination && !(totalRowCount >= 0)) {
    throw new Error('Invalid props: using updatePageData requires that totalRowCount be set');
  } else if (hasManualPagination && props.sortEnabled && !props.onSort) {
    throw new Error('Invalid props: using updatePageData requires that onSort be defined if sortEnabled==true');
  } else if (hasManualPagination && props.rowSelectionEnabled && !props.getRowId) {
    throw new Error('Invalid props: using updatePageData and rowSelectionEnabled requires that getRowId be set');
  }

  // * Helper function to get default text alignment
  const getAlignment = (align?: string, type?: string) => {
    if (align) {
      return align === 'right' ? 'override-right-align' : 'override-left-align';
    }
    switch (type) {
      case 'currency':
        return 'data-currency';
      default:
        return '';
    }
  };

  const data: T[] = useMemo(() => props.data, [props.data]);

  const defaultColumn = useMemo(
    () => ({
      // for useFlexLayout:
      minWidth: 35, // minWidth is only used as a limit for resizing
      width: 150, // width is used for both the flex-basis and flex-grow
      maxWidth: 200, // maxWidth is only used as a limit for resizing
    }),
    []
  );

  const columns: Column<T>[] = useMemo(() => {
    /**
     * Returns the equivalent React Table Column for any Aspect TableColumn
     * @param column
     */
    const convertReactTablesColumn = (column: TableColumn): Column<T> => {
      const reactTablesColumn: Column<T> = {
        ...column,
        Header: column.formatHeader ? column.formatHeader(column.name) : column.name,
        accessor: column.id as keyof T,
        id: column.id,
        disableSortBy: column.isSortable === false,
        columns: column.columns ? column.columns.map(convertReactTablesColumn) : undefined,
      };

      reactTablesColumn.Cell = (props: CellProps<T>) => {
        if (column.formatCell) {
          return column.formatCell(props.value, props.row.original);
        }
        return props.value;
      };
      return reactTablesColumn;
    };

    const cols = props.columns.map(convertReactTablesColumn);

    if (props.rowSelectionEnabled) {
      // Add a checkbox column for selections
      cols.unshift({
        id: 'selection',
        width: '2rem',
        // The header can use the table's getToggleAllRowsSelectedProps method
        // to render a checkbox
        Header: function renderHeaderCheckbox({ getToggleAllRowsSelectedProps }: HeaderProps<T>) {
          return <Checkbox {...getToggleAllRowsSelectedProps()} className="ps-table__checkbox" />;
        },
        // The cell can use the individual row's getToggleRowSelectedProps method
        // to the render a checkbox
        Cell: function renderRowCheckbox({ row }: { row: Row<T> }) {
          return <Checkbox {...row.getToggleRowSelectedProps()} className="ps-table__checkbox" />;
        },
      });
    }
    return cols;
  }, [props.columns, props.rowSelectionEnabled]);

  const plugins: PluginHook<T>[] = [
    useFlexLayout,
    useSticky,
    useResizeColumns,
    useSortBy,
    props.enablePagination ? usePagination : () => {},
    useRowSelect,
  ];

  let useTableProps = {
    columns,
    defaultColumn,
    data,
    manualSortBy: !!props.onSort,
    disableMultiSort: !!props.onSort,
    disableSortRemove: true,
    disableSortBy: !props.sortEnabled,
    initialState: {
      selectedRowIds: initialSelectedRowIds,
      pageIndex: paginationProps?.pageIndex ?? 0,
      pageSize: paginationProps?.pageSize ?? 10,
    },
    getRowId: props.getRowId,
    stateReducer: (newState: TableState<T>, action: { type: string; value?: boolean }) => {
      switch (action.type) {
        case 'toggleAllRowsSelected': {
          const selectedRowIds = Object.assign({}, newState.selectedRowIds);
          return {
            ...newState,
            selectedRowIds: action.value ? selectedRowIds : initialSelectedRowIds || {},
          };
        }
        default:
          return newState;
      }
    },
  };

  if (hasManualPagination) {
    useTableProps = Object.assign(useTableProps, {
      manualPagination: hasManualPagination,
      pageCount: paginationProps?.totalPageCount ?? -1,
      autoResetPage: false,
    });
  }

  if (hasManualPagination && props.rowSelectionEnabled) {
    useTableProps = Object.assign(useTableProps, {
      autoResetSelectedRows: false,
    });
  }

  const instance = useTable<T>(useTableProps, ...plugins);

  useEffect(() => {
    if (onTableInstanceChange) onTableInstanceChange(instance);
  }, [onTableInstanceChange, instance]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageCount,
    selectedFlatRows,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    toggleAllRowsSelected,
    state: { pageIndex, pageSize, sortBy, selectedRowIds },
  } = instance;

  // Handles manual sort callback
  useEffect(() => {
    if (props.onSort && sortBy) {
      if (sortBy.length > 0 && sortBy[0].desc !== undefined) {
        const col = sortBy[0].id;
        const dir = sortBy[0].desc ? 'descending' : 'ascending';
        // Only trigger callback if the sort has changed
        if (sortColumn !== col || sortDirection !== dir) {
          setSortColumn(col);
          setSortDirection(dir);
          props.onSort(col, dir);
          if (hasManualPagination) {
            gotoPage(0);
          }
        }
      }
    }
  }, [props.onSort, gotoPage, hasManualPagination, sortBy, props, sortColumn, sortDirection]);

  // Handles updating the pagination input value whenever the current page changes
  useEffect(() => {
    setPaginationInputValue((pageIndex + 1).toString());
  }, [pageIndex]);

  const previousSelectedRowIds = usePrevious(selectedRowIds);

  useMountedLayoutEffect(() => {
    const selectedIds = Object.keys(selectedRowIds);
    if (selectedIds.length === Object.keys(previousSelectedRowIds ?? {}).length) {
      return;
    }

    const selectedRowsData = selectedIds
      .map((x: unknown) => data[x as number])
      .filter(function (x) {
        return x != null;
      });

    onSelectRow?.(selectedRowsData);
  }, [data, onSelectRow, selectedRowIds]);

  // Handles calling updatePageData whenever the current page or page size changes
  useDidMountEffect(() => {
    if (onPaginate) onPaginate(pageIndex, pageSize);
  }, [pageIndex, pageSize]);

  useDidMountEffect(() => {
    if (updatePageData) {
      updatePageData(pageIndex, pageSize);
      if (pageIndex !== paginationProps?.pageIndex || pageSize !== paginationProps?.pageSize) {
        gotoPage(paginationProps?.pageIndex || 0);
        setPageSize(paginationProps?.pageSize || 50);
      }
    }
  }, [updatePageData]);

  const setPageSizeWrapper = (newPageSize: number) => {
    setPageSize(newPageSize);
  };

  const handleOnAction = (onAction: (rows: T[], rowIds?: string[]) => void, clearSelections?: boolean) => () => {
    // call the action callback
    onAction(
      selectedFlatRows?.map((d) => d.original),
      Object.keys(selectedRowIds)
    );
    if (clearSelections) {
      // and clear all the selections
      toggleAllRowsSelected(false);
    }
  };

  // Pagination event handler wrappers
  const handlePaginationInputOnChange = (event: React.ChangeEvent<HTMLInputElement>, newValue?: string | number) =>
    setPaginationInputValue(newValue ?? '');
  const handlePaginationInputOnKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') gotoPaginationInputPage();
  };
  const handlePaginationInputOnBlur = () => gotoPaginationInputPage();
  const handlePaginationInputOnClick = () => gotoPaginationInputPage();
  const handleLastPageButtonOnClick = () => gotoPage(pageCount - 1);
  const handleFirstPageButtonOnClick = () => gotoPage(0);

  const gotoPaginationInputPage = () => {
    const toPage = parseInt(paginationInputValue?.toString()) - 1;
    if (toPage <= 0) {
      if (pageIndex === 0) setPaginationInputValue('1');
      gotoPage(0);
    } else if (toPage >= pageCount) {
      if (pageIndex === pageCount - 1) setPaginationInputValue(pageCount.toString());
      gotoPage(pageCount - 1);
    } else {
      gotoPage(toPage);
    }
  };

  const pageSizeOptions = (paginationProps?.pageSizeOptions ?? [10, 50, 100]).map((sizeOption) => {
    return { key: sizeOption.toString(), text: sizeOption.toString() };
  });

  const totalLength = paginationProps?.totalRowCount ?? data.length;
  const pageIsLoading = paginationProps?.pageIsLoading;

  const containerClassName = classNames(
    'ps-table',
    props.className,
    props.rowStripingType === 'striped-even' ? 'ps-table--striped-even' : '',
    props.rowStripingType === 'striped-odd' ? 'ps-table--striped-odd' : '',
    props.fixedHeader ? 'ps-table--sticky-header' : '',
    props.toolbar ? 'ps-table--sticky-header-toolbar' : ''
  );

  const paginationBackButtonClassName = classNames(
    'ps-pagination__button',
    'ps-pagination__button--back',
    canPreviousPage ? '' : 'ps-pagination__button--disabled'
  );

  const paginationForwardButtonClassName = classNames(
    'ps-pagination__button',
    'ps-pagination__button--forward',
    canNextPage ? '' : 'ps-pagination__button--disabled'
  );

  const toolbarClassNames = classNames(
    'ps-toolbar',
    selectedFlatRows.length === 0 && props.toolbar?.isHiddenUntilSelection ? 'ps-toolbar--hidden' : '',
    props.fixedHeader ? '' : 'ps-toolbar--shadow'
  );

  const getLoadingRow = () => {
    return (
      <div className="ps-table__loading-row">
        <ContentLoader>
          <rect x="0" y="0" rx="3" ry="3" />
        </ContentLoader>
      </div>
    );
  };

  const getLoadedRow = (row: Row<T>, className: string) => {
    return (
      <div {...row.getRowProps()} className={className} key={row.getRowProps().key}>
        {row.cells.map((cell) => {
          const cellClassName = classNames(
            'ps-table__cell',
            props.compact ? 'ps-table__cell--compact' : '',
            !cell.column.formatCell ? getAlignment(cell.column.contentAlign, cell.column.dataType) : '',
            cell.column.columnClassName
          );
          return (
            <div
              {...cell.getCellProps([{ className: cellClassName }])}
              key={cell.getCellProps([{ className: cellClassName }]).key}
            >
              {cell.render('Cell')}
            </div>
          );
        })}
      </div>
    );
  };

  if (data.length === 0 && !pageIsLoading && props?.emptyComponent) return props.emptyComponent;

  /**
   * selectedFlatRows only includes rows from the current page when using server-side pagination.
   */
  const rowsSelected =
    hasManualPagination && props.rowSelectionEnabled ? Object.keys(selectedRowIds).length : selectedFlatRows.length;

  // Ignoring keys in the following JSX because react-table generates them for us.
  return (
    <>
      <div {...getTableProps()} className={containerClassName}>
        <div className="ps-table__header">
          {props.rowSelectionEnabled && props.toolbar && (
            <div className="ps-toolbar-wrapper">
              <div className={toolbarClassNames}>
                <div className="ps-toolbar__label">{`${rowsSelected} Selected`}</div>
                {props.toolbar.actions.map((option, index) => {
                  const selectedRows = selectedFlatRows?.map((d) => d.original);
                  return (
                    <div className="ps-toolbar__option" key={`${option.actionName}${index}`}>
                      <Button
                        variant="icon-link"
                        icon={option.actionIconName ? option.actionIconName : ''}
                        disabled={
                          rowsSelected === 0 || (option.isActionEnabled && !option.isActionEnabled(selectedRows))
                        }
                        onClick={handleOnAction(option.onAction, option.clearSelections)}
                      >
                        {option.actionName}
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {headerGroups.map((headerGroup) => (
            <div {...headerGroup.getHeaderGroupProps()} key={headerGroup.getHeaderGroupProps().key}>
              {headerGroup.headers.map((column) => {
                const headerProps: TableHeaderProps = column.getHeaderProps() as TableHeaderProps;
                const headerClassName = classNames(
                  'ps-table__cell',
                  'ps-table__column',
                  !column.formatHeader ? getAlignment(column.contentAlign, column.dataType) : '',
                  column.headerClassName
                );
                return (
                  <div
                    {...headerProps}
                    style={{ ...headerProps.style, position: headerProps['data-sticky-td'] ? 'sticky' : 'relative' }}
                    className={headerClassName}
                    key={headerProps.key}
                  >
                    {props.sortEnabled ? (
                      <span {...column.getSortByToggleProps()}>
                        {column.render('Header')}
                        <span
                          className={classNames(
                            'ps-table__column-icon-area',
                            column.contentAlign === 'right' ? 'ps-table__column-icon-right-align' : ''
                          )}
                        >
                          {column.canSort ? (
                            column.isSorted ? (
                              <SortIcon direction={column.isSortedDesc ? 'down' : 'up'} />
                            ) : (
                              <SortIcon />
                            )
                          ) : (
                            ''
                          )}
                        </span>
                      </span>
                    ) : (
                      <span>{column.render('Header')}</span>
                    )}
                    {/** ToDo: implement a resizer bar here, once a design for that exists. */}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        <div {...getTableBodyProps()} className="ps-table--body">
          {(page ?? rows).map((row) => {
            prepareRow(row);
            const className = classNames(
              'ps-table__row',
              props.rowSelectionEnabled ? 'ps-table__row--selectable' : '',
              props.rowSelectionEnabled && row.isSelected ? 'ps-table__row--selected' : '',
              row.original.className
            );
            return pageIsLoading ? getLoadingRow() : getLoadedRow(row, className);
          })}
        </div>
      </div>
      {props.enablePagination && (
        <div className="ps-pagination">
          <div className="ps-pagination__size-select">
            <span>Rows per page:</span>
            <Select
              value={pageSize.toString()}
              options={pageSizeOptions}
              dropUp={paginationProps?.dropUp}
              componentRef={pageSizeOptionsSelectRef}
              inline={true}
              handleChange={(value) => {
                setPageSizeWrapper(parseInt(value));
              }}
              className="ps-pagination__select"
            />
          </div>
          <div className="ps-pagination__controls">
            <Button
              disabled={!canPreviousPage}
              variant="link"
              aria-label="first page"
              onClick={handleFirstPageButtonOnClick}
              className={paginationBackButtonClassName}
            >
              &laquo;
            </Button>
            <Button
              disabled={!canPreviousPage}
              variant="link"
              aria-label="previous page"
              onClick={previousPage}
              className={paginationBackButtonClassName}
            >
              &lsaquo;
            </Button>
            <span>Page</span>
            <Input
              aria-label={`current page, page ${paginationInputValue}`}
              type="number"
              ref={paginationInputRef}
              value={paginationInputValue}
              onBlur={handlePaginationInputOnBlur}
              onChange={handlePaginationInputOnChange}
              onKeyDown={handlePaginationInputOnKeyDown}
              onClick={handlePaginationInputOnClick}
              className="ps-pagination__input"
            />
            <span>of {pageCount}</span>
            <Button
              disabled={!canNextPage}
              variant="link"
              aria-label="next page"
              onClick={nextPage}
              className={paginationForwardButtonClassName}
            >
              &rsaquo;
            </Button>
            <Button
              variant="link"
              disabled={!canNextPage}
              aria-label="last page"
              onClick={handleLastPageButtonOnClick}
              className={paginationForwardButtonClassName}
            >
              &raquo;
            </Button>
          </div>
          <span className="ps-pagination__info">
            {pageIsLoading ? 'Loading' : 'Showing'} {pageIndex * pageSize + 1}-
            {Math.min((pageIndex + 1) * pageSize, totalLength)} of {totalLength}
          </span>
        </div>
      )}
    </>
  );
}
